import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import { User, Mail, CreditCard, Shield, Bell, ChevronRight } from 'lucide-react';
import ConfirmationModal from '../components/ConfirmationModal';
import '../styles/index.css';

export default function AccountSettings() {
    const navigate = useNavigate();
    const { user, creditBalance, subscriptionStatus, subscriptionTier, formatCredits, creditsToUSD } = useApp();
    const { currentUser, logout } = useAuth();
    const [activeTab, setActiveTab] = useState('profile');
    const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
    const [notificationSettings, setNotificationSettings] = useState({
        emailNotifications: true,
        pushNotifications: true,
        weeklyDigest: false,
        campaignAlerts: true,
        lowBalanceAlerts: true,
    });

    const handleSignOut = async () => {
        try {
            await logout();
            navigate('/login');
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    const tabs = [
        { id: 'profile', label: 'Profile', icon: User },
        { id: 'billing', label: 'Billing & Subscription', icon: CreditCard },
        { id: 'notifications', label: 'Notifications', icon: Bell },
        { id: 'security', label: 'Security', icon: Shield },
    ];

    return (
        <div className="page-container">
            <div className="page-header">
                <div>
                    <h1>Account Settings</h1>
                    <p className="page-subtitle">Manage your account details, billing, and preferences</p>
                </div>
            </div>

            <div style={{ display: 'flex', gap: 'var(--space-6)', flexWrap: 'wrap' }}>
                {/* Sidebar Navigation */}
                <div style={{ width: '240px', flexShrink: 0 }}>
                    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-3)',
                                    width: '100%',
                                    padding: 'var(--space-4)',
                                    background: activeTab === tab.id ? 'var(--color-primary-light)' : 'transparent',
                                    border: 'none',
                                    borderLeft: activeTab === tab.id ? '3px solid var(--color-primary)' : '3px solid transparent',
                                    cursor: 'pointer',
                                    textAlign: 'left',
                                    color: activeTab === tab.id ? 'var(--color-primary)' : 'var(--color-text-primary)',
                                    fontWeight: activeTab === tab.id ? 600 : 400,
                                    fontSize: 'var(--font-size-sm)',
                                    transition: 'all var(--transition-fast)',
                                }}
                                className="account-tab-btn"
                            >
                                <tab.icon size={18} />
                                <span>{tab.label}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Content Area */}
                <div style={{ flex: 1, minWidth: '300px' }}>
                    {/* Profile Tab */}
                    {activeTab === 'profile' && (
                        <div className="card">
                            <h2 style={{ marginBottom: 'var(--space-6)' }}>Profile Information</h2>

                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', marginBottom: 'var(--space-6)' }}>
                                <div
                                    className="avatar"
                                    style={{
                                        width: '80px',
                                        height: '80px',
                                        fontSize: 'var(--font-size-2xl)',
                                        background: 'var(--gradient-primary)',
                                        color: 'white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: 'var(--radius-full)',
                                    }}
                                >
                                    {user.avatar ? (
                                        <img src={user.avatar} alt={user.name} style={{ width: '100%', height: '100%', borderRadius: 'var(--radius-full)' }} />
                                    ) : (
                                        user.name?.split(' ').map(n => n[0]).join('').toUpperCase()
                                    )}
                                </div>
                                <div>
                                    <h3 style={{ margin: 0 }}>{user.name}</h3>
                                    <p style={{ margin: 0, color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
                                        {user.role}
                                    </p>
                                </div>
                            </div>

                            <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-2)' }}>
                                        Display Name
                                    </label>
                                    <input
                                        type="text"
                                        defaultValue={user.name}
                                        className="input"
                                        style={{ width: '100%' }}
                                    />
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-2)' }}>
                                        Email Address
                                    </label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                        <Mail size={18} style={{ color: 'var(--color-text-tertiary)' }} />
                                        <span>{currentUser?.email || user.email}</span>
                                    </div>
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-2)' }}>
                                        Role
                                    </label>
                                    <span style={{
                                        display: 'inline-block',
                                        padding: 'var(--space-1) var(--space-3)',
                                        background: 'var(--color-primary-light)',
                                        color: 'var(--color-primary)',
                                        borderRadius: 'var(--radius-full)',
                                        fontSize: 'var(--font-size-sm)',
                                        fontWeight: 500,
                                    }}>
                                        {user.role}
                                    </span>
                                </div>
                            </div>

                            <div style={{ marginTop: 'var(--space-6)', paddingTop: 'var(--space-6)', borderTop: '1px solid var(--color-border)' }}>
                                <button className="btn btn-primary">Save Changes</button>
                            </div>
                        </div>
                    )}

                    {/* Billing Tab */}
                    {activeTab === 'billing' && (
                        <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
                            {/* Current Balance Card */}
                            <div className="card" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div>
                                        <h3 style={{ margin: 0, marginBottom: 'var(--space-2)', color: 'white', opacity: 0.9 }}>Current Balance</h3>
                                        <div style={{ fontSize: '2rem', fontWeight: 700, marginBottom: 'var(--space-1)' }}>
                                            {formatCredits(creditBalance)} credits
                                        </div>
                                        <div style={{ fontSize: '1rem', opacity: 0.9 }}>
                                            ≈ ${creditsToUSD(creditBalance)} USD
                                        </div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontSize: '0.85rem', opacity: 0.9, marginBottom: 'var(--space-1)' }}>Subscription</div>
                                        <div style={{
                                            fontSize: '1rem',
                                            fontWeight: 600,
                                            padding: 'var(--space-1) var(--space-3)',
                                            background: 'rgba(255, 255, 255, 0.2)',
                                            borderRadius: 'var(--radius-md)',
                                            textTransform: 'capitalize'
                                        }}>
                                            {subscriptionTier || 'Free'} • {subscriptionStatus || 'Inactive'}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Billing Actions */}
                            <div className="card">
                                <h2 style={{ marginBottom: 'var(--space-4)' }}>Billing & Subscription</h2>

                                <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
                                    <button
                                        onClick={() => navigate('/billing')}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: 'var(--space-4)',
                                            background: 'var(--color-bg-secondary)',
                                            border: '1px solid var(--color-border)',
                                            borderRadius: 'var(--radius-lg)',
                                            cursor: 'pointer',
                                            transition: 'all var(--transition-fast)',
                                        }}
                                        className="billing-action-btn"
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                                            <CreditCard size={20} style={{ color: 'var(--color-primary)' }} />
                                            <div style={{ textAlign: 'left' }}>
                                                <div style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>Purchase Credits</div>
                                                <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                                                    Buy credit packages or manage subscription
                                                </div>
                                            </div>
                                        </div>
                                        <ChevronRight size={20} style={{ color: 'var(--color-text-tertiary)' }} />
                                    </button>

                                    <button
                                        onClick={() => navigate('/usage-history')}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: 'var(--space-4)',
                                            background: 'var(--color-bg-secondary)',
                                            border: '1px solid var(--color-border)',
                                            borderRadius: 'var(--radius-lg)',
                                            cursor: 'pointer',
                                            transition: 'all var(--transition-fast)',
                                        }}
                                        className="billing-action-btn"
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                                            <CreditCard size={20} style={{ color: 'var(--color-primary)' }} />
                                            <div style={{ textAlign: 'left' }}>
                                                <div style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>Usage History</div>
                                                <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                                                    View your credit usage and transaction history
                                                </div>
                                            </div>
                                        </div>
                                        <ChevronRight size={20} style={{ color: 'var(--color-text-tertiary)' }} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Notifications Tab */}
                    {activeTab === 'notifications' && (
                        <div className="card">
                            <h2 style={{ marginBottom: 'var(--space-6)' }}>Notification Preferences</h2>

                            <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
                                {[
                                    { key: 'emailNotifications', label: 'Email Notifications', description: 'Receive notifications via email' },
                                    { key: 'pushNotifications', label: 'Push Notifications', description: 'Receive push notifications in browser' },
                                    { key: 'weeklyDigest', label: 'Weekly Digest', description: 'Receive a weekly summary of your activity' },
                                    { key: 'campaignAlerts', label: 'Campaign Alerts', description: 'Get notified about campaign performance changes' },
                                    { key: 'lowBalanceAlerts', label: 'Low Balance Alerts', description: 'Get notified when your credit balance is low' },
                                ].map((setting) => (
                                    <div
                                        key={setting.key}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: 'var(--space-3)',
                                            background: 'var(--color-bg-secondary)',
                                            borderRadius: 'var(--radius-lg)',
                                        }}
                                    >
                                        <div>
                                            <div style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>{setting.label}</div>
                                            <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                                                {setting.description}
                                            </div>
                                        </div>
                                        <label className="toggle-switch">
                                            <input
                                                type="checkbox"
                                                checked={notificationSettings[setting.key]}
                                                onChange={(e) => setNotificationSettings({
                                                    ...notificationSettings,
                                                    [setting.key]: e.target.checked
                                                })}
                                            />
                                            <span className="toggle-slider"></span>
                                        </label>
                                    </div>
                                ))}
                            </div>

                            <div style={{ marginTop: 'var(--space-6)', paddingTop: 'var(--space-6)', borderTop: '1px solid var(--color-border)' }}>
                                <button className="btn btn-primary">Save Preferences</button>
                            </div>
                        </div>
                    )}

                    {/* Security Tab */}
                    {activeTab === 'security' && (
                        <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
                            <div className="card">
                                <h2 style={{ marginBottom: 'var(--space-4)' }}>Password</h2>
                                <p style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--space-4)' }}>
                                    Change your password to keep your account secure.
                                </p>
                                <button className="btn btn-secondary">Change Password</button>
                            </div>

                            <div className="card">
                                <h2 style={{ marginBottom: 'var(--space-4)' }}>Sessions</h2>
                                <p style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--space-4)' }}>
                                    Manage your active sessions and sign out from other devices.
                                </p>
                                <div style={{
                                    padding: 'var(--space-3)',
                                    background: 'var(--color-bg-secondary)',
                                    borderRadius: 'var(--radius-lg)',
                                    marginBottom: 'var(--space-4)',
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <div>
                                            <div style={{ fontWeight: 500 }}>Current Session</div>
                                            <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                                                This device • Active now
                                            </div>
                                        </div>
                                        <span style={{
                                            padding: 'var(--space-1) var(--space-2)',
                                            background: 'var(--color-success-light)',
                                            color: 'var(--color-success)',
                                            borderRadius: 'var(--radius-full)',
                                            fontSize: 'var(--font-size-xs)',
                                            fontWeight: 500,
                                        }}>
                                            Active
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="card" style={{ borderColor: 'var(--color-error-light)' }}>
                                <h2 style={{ marginBottom: 'var(--space-4)', color: 'var(--color-error)' }}>Sign Out</h2>
                                <p style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--space-4)' }}>
                                    Sign out of your account on this device.
                                </p>
                                <button
                                    onClick={() => setShowSignOutConfirm(true)}
                                    className="btn"
                                    style={{ background: 'var(--color-error)', color: 'white' }}
                                >
                                    Sign Out
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <ConfirmationModal
                isOpen={showSignOutConfirm}
                onClose={() => setShowSignOutConfirm(false)}
                onConfirm={handleSignOut}
                title="Sign Out?"
                message="Are you sure you want to sign out of your account?"
                confirmText="Sign Out"
                cancelText="Cancel"
                variant="danger"
            />

            <style>{`
                .account-tab-btn:hover {
                    background: var(--color-surface-hover) !important;
                }

                .billing-action-btn:hover {
                    background: var(--color-surface-hover) !important;
                    border-color: var(--color-primary) !important;
                }

                .toggle-switch {
                    position: relative;
                    display: inline-block;
                    width: 48px;
                    height: 24px;
                }

                .toggle-switch input {
                    opacity: 0;
                    width: 0;
                    height: 0;
                }

                .toggle-slider {
                    position: absolute;
                    cursor: pointer;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-color: var(--color-border);
                    transition: 0.3s;
                    border-radius: 24px;
                }

                .toggle-slider:before {
                    position: absolute;
                    content: "";
                    height: 18px;
                    width: 18px;
                    left: 3px;
                    bottom: 3px;
                    background-color: white;
                    transition: 0.3s;
                    border-radius: 50%;
                }

                .toggle-switch input:checked + .toggle-slider {
                    background-color: var(--color-primary);
                }

                .toggle-switch input:checked + .toggle-slider:before {
                    transform: translateX(24px);
                }
            `}</style>
        </div>
    );
}
